import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import { push, ref, serverTimestamp } from 'firebase/database';
import { observable, reaction } from 'mobx';
import { BaseFirebaseStore } from './BaseFirebaseStore';
import { useStoreUI } from './Store';

export interface ThinkingEntry {
    type: string;
    content: string;
}

type ThinkingHistory = ThinkingEntry[];

export interface SideChatMessage {
    isMe: boolean;
    type: string;
    content: any;
    thinking: ThinkingHistory;
    createdAt: object;
    chatContext: string;
    fullChatContext: string;
}

export type SideChatJobState =
    | 'queued'
    | 'in_progress'
    | 'completed'
    | 'failed';

interface SideChatJob {
    chatContext: string;
    fullChatContext: string;
    reportId: string;
    status: SideChatJobState;
    createdAt: object;
}

class dbPathsSideChat {
    constructor(private reportId: string) {}
    chatMessagesForContext(contextId: string): string {
        return `sidechat/reports/${this.reportId}/${contextId}`;
    }

    jobsQueue(): string {
        return `sidechat/jobs`;
    }
}

export type LoadingStatus = 'loading' | 'ready';

export class SideChatStore extends BaseFirebaseStore {
    private dbPaths: dbPathsSideChat;
    private mListenContext: string = null;

    @observable accessor status: LoadingStatus = 'ready';
    @observable accessor chatMessages: SideChatMessage[] = [];
    private mReactionUnsub: () => void = null;
    constructor(private reportId: string) {
        super();
        this.dbPaths = new dbPathsSideChat(reportId);

        this.setupMobxReactions();
    }

    override unregister(): void {
        super.unregister();
        if (this.mReactionUnsub) {
            this.mReactionUnsub();
            this.mReactionUnsub = null;
        }
    }

    private set_loading() {
        this.status = 'loading';
        this.chatMessages = [];
    }

    private setupMobxReactions() {
        const uiStore = useStoreUI();
        this.mReactionUnsub = reaction(
            () => uiStore.sideChatSelectedContext,
            chatContext => {
                const path = this.dbPaths.chatMessagesForContext(chatContext);
                this.mListenContext = chatContext;

                this.set_loading();
                this.listenOnPath(path, (data, snapshot) => {
                    const orderedMessages = [];
                    snapshot.forEach(childSnapshot => {
                        orderedMessages.push({ ...childSnapshot.val() });
                    });
                    this.chatMessages = orderedMessages;

                    //If last message is changing color sideChatIsNextActionChangingData
                    const ui = useStoreUI();
                    if (this.chatMessages && this.chatMessages.length > 0) {
                        const last = this.chatMessages.at(-1);
                        if (!last.isMe && last.type != 'text') {
                            ui.sideChatIsNextActionChangingData = true;
                        } else {
                            ui.sideChatIsNextActionChangingData = false;
                        }
                    } else {
                        ui.sideChatIsNextActionChangingData = false;
                    }
                    this.status = 'ready';
                });
            },
            { fireImmediately: true }
        );
    }

    send_message(content: string, fullChatContextPath: string) {
        const path = this.dbPaths.chatMessagesForContext(this.mListenContext);
        const msg: SideChatMessage = {
            isMe: true,
            thinking: [],
            content: content,
            type: 'text',
            createdAt: serverTimestamp(),
            chatContext: this.mListenContext,
            fullChatContext: fullChatContextPath
        };

        const fb = getFirebase();
        let r = ref(fb.db, path);
        push(r, msg);

        const path_job = ref(fb.db, this.dbPaths.jobsQueue());
        const job: SideChatJob = {
            chatContext: this.mListenContext,
            fullChatContext: fullChatContextPath,
            reportId: this.reportId,
            status: 'queued',
            createdAt: serverTimestamp()
        };
        push(path_job, job);
    }
}
